import { createContext, useContext, useState, useMemo, lazy, Suspense, ReactNode } from "react";

const RequestPartnershipModal = lazy(() => import("@/components/landing/RequestPartnershipModal"));
const SandboxPopup = lazy(() => import("@/components/landing/SandboxPopup"));
const SimulatorModal = lazy(() => import("@/components/SimulatorModal"));

interface ModalState {
  open: boolean;
  setOpen: (open: boolean) => void;
}

interface ModalContextValue {
  partnership: ModalState;
  sandbox: ModalState;
  harmony: ModalState;
  patterns: ModalState;
  pricing: ModalState;
}

const ModalContext = createContext<ModalContextValue | null>(null);

export const ModalProvider = ({ children }: { children: ReactNode }) => {
  const [partnershipOpen, setPartnershipOpen] = useState(false);
  const [sandboxOpen, setSandboxOpen] = useState(false);
  const [harmonyOpen, setHarmonyOpen] = useState(false);
  const [patternsOpen, setPatternsOpen] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);

  const value = useMemo<ModalContextValue>(
    () => ({
      partnership: { open: partnershipOpen, setOpen: setPartnershipOpen },
      sandbox: { open: sandboxOpen, setOpen: setSandboxOpen },
      harmony: { open: harmonyOpen, setOpen: setHarmonyOpen },
      patterns: { open: patternsOpen, setOpen: setPatternsOpen },
      pricing: { open: pricingOpen, setOpen: setPricingOpen },
    }),
    [partnershipOpen, sandboxOpen, harmonyOpen, patternsOpen, pricingOpen]
  );

  return (
    <ModalContext.Provider value={value}>
      {children}
      <Suspense fallback={null}>
        {partnershipOpen && (
          <RequestPartnershipModal open={partnershipOpen} onOpenChange={setPartnershipOpen} />
        )}
        {sandboxOpen && <SandboxPopup open={sandboxOpen} onOpenChange={setSandboxOpen} />}
        {harmonyOpen && (
          <SimulatorModal
            open={harmonyOpen}
            onOpenChange={setHarmonyOpen}
            src="/simulators/Harmony.html"
            title="ClearSlot Coordination Demo"
          />
        )}
        {patternsOpen && (
          <SimulatorModal
            open={patternsOpen}
            onOpenChange={setPatternsOpen}
            src="/simulators/Patterns.html"
            title="ClearSlot Scoring Formula"
          />
        )}
        {pricingOpen && (
          <SimulatorModal
            open={pricingOpen}
            onOpenChange={setPricingOpen}
            src="/simulators/Pricing.html"
            title="ClearSlot Pricing Calculator"
          />
        )}
      </Suspense>
    </ModalContext.Provider>
  );
};

const useModalContext = () => {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error("Modal hooks must be used within a ModalProvider");
  return ctx;
};

export const useOpenPartnership = (): ModalState => useModalContext().partnership;
export const useOpenSandbox = (): ModalState => useModalContext().sandbox;
export const useOpenHarmony = (): ModalState => useModalContext().harmony;
export const useOpenPatterns = (): ModalState => useModalContext().patterns;
export const useOpenPricing = (): ModalState => useModalContext().pricing;
